import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetPaginated } from '../fetcher';
import type {
	SearchQcCustomerOptionResponse,
	SearchQcCustomerRequest,
	SearchQcCustomerItem,
	QcCustomerDetailResponse,
	SaveQcCustomerRequest,
} from './qc-customer-types';

// ─── Search Options (GET) ───────────────────────────────────────────────────

export const getQcCustomerSearchOptions = () =>
	fetcher<SearchQcCustomerOptionResponse>({
		api: '/quality-control-customer/get-search-options',
		method: 'GET',
	});

export const useQcCustomerSearchOptions = () =>
	useQuery({
		queryKey: ['getQcCustomerSearchOptions'] as const,
		queryFn: () => getQcCustomerSearchOptions(),
	});

// ─── Search (POST) ──────────────────────────────────────────────────────────

export const searchQcCustomers = (params: SearchQcCustomerRequest) =>
	fetcher<FleetPaginated<SearchQcCustomerItem>>({
		api: '/quality-control-customer/search',
		body: params,
	});

export const useQcCustomerSearch = (params: SearchQcCustomerRequest) =>
	useQuery({
		queryKey: ['searchQcCustomers', params] as const,
		queryFn: () => searchQcCustomers(params),
	});

// ─── Detail (POST) ──────────────────────────────────────────────────────────

export const getQcCustomerDetail = (customerId: string) =>
	fetcher<QcCustomerDetailResponse>({
		api: '/quality-control-customer/get-detail',
		body: { customerId },
	});

export const useQcCustomerDetail = (customerId: string) =>
	useQuery({
		queryKey: ['getQcCustomerDetail', customerId] as const,
		queryFn: () => getQcCustomerDetail(customerId),
		enabled: !!customerId,
	});

// ─── Save (POST, action=1) ─────────────────────────────────────────────────

export const saveQcCustomer = (payload: SaveQcCustomerRequest) =>
	fetcher<void>({
		api: '/quality-control-customer/save',
		body: payload,
	});

export const useSaveQcCustomer = () =>
	useMutation({ mutationFn: saveQcCustomer });

// ─── Submit (POST, action=2) ────────────────────────────────────────────────

export const submitQcCustomer = (payload: SaveQcCustomerRequest) =>
	fetcher<void>({
		api: '/quality-control-customer/submit',
		body: payload,
	});

export const useSubmitQcCustomer = () =>
	useMutation({ mutationFn: submitQcCustomer });
